<template>
    <div>
        <breadcrumbs
            :text="company.name[company.language] || $t('create_new_company')"
            :company="company"
            class="mb-3"
        />
        <div v-if="company?.name" class="text-h5 font-weight-bold py-2">
            {{ company.name[company.language] }}
        </div>
        <v-tabs v-model="selectedTabIndex" class="mb-4">
            <v-tab v-for="tab in tabs" :key="tab.name">
                {{ tab.label }}
            </v-tab>
        </v-tabs>
        <v-window v-model="selectedTabIndex" class="overflow--visible">
            <v-window-item v-for="tab in tabs" :key="tab.name">
                <supplier-profile v-if="tab.name === 'info'" :company-data="company" />
                <orders-page
                    v-if="tab.name === 'reservations'"
                    ref="orders"
                    company-page-mode
                    @new-order-request-close="onNewOrderRequestClose"
                >
                    <template v-slot:breadcrumbs><div /> </template>
                    <template v-slot:agentsPaymentsUpload><div /> </template>
                    <template v-slot:bookButtons>
                        <div class="d-flex align-center pb-4">
                            <v-btn class="bg-primary" size="small" @click="searchAndBook">
                                {{ $t('new_order') }}
                            </v-btn>
                        </div>
                    </template>
                </orders-page>
                <template v-if="tab.name === 'users'">
                    <div v-if="company.type !== 'HUB_SUPPLIER'" class="d-flex mb-4 align-center">
                        <v-btn
                            :to="{name: 'supplierPerson', params: {id: 0, companyId: company.id}}"
                            class="bg-primary me-2"
                        >
                            {{ $t('new_user_profile') }}
                        </v-btn>
                        <file-download-modal
                            v-if="isTO1 && company.type !== 'CORPORATOR'"
                            v-model="isShowDownloadExcelMenu"
                            :radio-items="downloadRadioItems"
                            :label="$t('users_download')"
                            :loading="isLoadingDownloadExcel"
                            @change-radio-group="radioGroupDownloadExcel = $event"
                            @download="downloadFile"
                        />
                        <file-upload-modal
                            v-if="isTO1 && company.type !== 'CORPORATOR'"
                            v-model="isShowUploadExcelMenu"
                            class="ms-4"
                            :label="$t('users_upload')"
                            :sample-link="sampleLink"
                            @upload="uploadExcel"
                        />
                    </div>
                    <persons-table
                        :company-id="company.id"
                        :company="company"
                        :_headers="headers"
                        person-type="supplierPerson"
                    />
                </template>
                <template v-if="tab.name === 'communications'">
                    <communications
                        :communications="communications"
                        :communications-loading="communicationsLoading"
                        :communications-total-count="communicationsCount"
                        :communication-data-for-create="{
                            accountId: company.id,
                        }"
                        @get-communications="getCommunications"
                    >
                        <template v-slot="{getCommunications}">
                            <communications-filters
                                v-model="communicationFilters"
                                :has-active-filters="hasActiveCommunicationFilters"
                                company-page-mode
                                @submit="getCommunications"
                                @reset="resetCommunicationFilters(getCommunications)"
                            />
                        </template>
                    </communications>
                </template>
                <template v-if="tab.name === 'documents'">
                    <DocumentsTable
                        ref="dataTable"
                        :company-id="company.id"
                        :attach-to-id="company.id"
                        :document-types="useCompanyDocument"
                    />
                </template>
                <template v-if="tab.name === 'offices'">
                    <OfficeItems :company-id="company.id" />
                </template>
                <template v-if="tab.name === 'notificationSettings'">
                    <NotificationSettingsForm :company-id="company.id" :is-my-company-notifications="isMyCompanyPage" />
                </template>
            </v-window-item>
        </v-window>
    </div>
</template>

<script lang="ts">
    import {Component} from 'vue-facing-decorator'
    import {CompanyProfilePage} from '~/pages/account/CompanyProfilePage.vue'
    import SupplierProfile from '@/components/account/SupplierProfile.vue'
    import {initCompanyProfile} from '~/utils/types/account/companies/helpers'
    import {useRecentViewsData} from '~/composables/recent-views'
    import OfficeItems from '~/components/account/offices/OfficeItems.vue'
    import NotificationSettingsForm from '~/components/account/clients/NotificationSettingsForm.vue'

    @Component({
        components: {
            NotificationSettingsForm,
            OfficeItems,
            SupplierProfile,
        },
        async setup() {
            const app = useNuxtApp()
            const $route = useRoute()
            const {data} = await useAsyncData(`company-${$route.params.id}`, async () => {
                return await CompanySupplierPage.__vccOpts.methods.asyncData({$api: app.$api, params: $route.params})
            })
            if (data.value) {
                const company = data.value
                const lang = 'en'
                const linkName = company.name[lang]
                const companyType = company.type
                if (companyType && linkName) await useRecentViewsData($route, linkName, companyType)
            }
            return {company: data.value}
        },
    })
    export class CompanySupplierPage extends CompanyProfilePage {
        mod = 'supplier-page-mode'

        mounted() {
            this.tempFilters = persistentStore.orderFilters
            persistentStore.CLEAR_ORDER_FILTERS()
            persistentStore.SET_ORDER_FILTERS(
                Object.assign({}, persistentStore.orderFilters, {supplierId: this.company.id, filterServices: true})
            )
        }

        get tabs() {
            let tabs = super.tabs
            if (this.company?.type === 'HUB_SUPPLIER') {
                tabs = tabs.filter(({name}) => name !== 'users')
            }
            tabs = tabs.filter(({name}) => name !== 'documents')
            tabs = tabs.filter(({name}) => name !== 'communications')
            // Notification settings tab is pushed here, not in the shared CompanyProfilePage getter:
            // only this page has the panel. Putting it in the parent advertised a dead header (no
            // panel, no request) on the supplier's own-profile route (/agency/profile → CompanyProfilePage).
            if (this.company?.type === 'INTERNAL_SUPPLIER') {
                tabs.push({name: 'notificationSettings', label: this.$t('notification_settings')})
            }
            return tabs
        }

        beforeUnmount() {
            persistentStore.CLEAR_ORDER_FILTERS()
        }

        async asyncData({$api, params}) {
            if (params.id === '0') return initCompanyProfile()
            if (params.id) {
                const id = params.id
                try {
                    const rq = {id, relationship: 'SUPPLIER', shortResponse: false, locale: 'all'}
                    const company = (await $api.companies.get(rq)).companies[0]
                    if (!company.contactPerson) {
                        company.contactPerson = {}
                    }
                    return company
                } catch (e) {
                    return initCompanyProfile()
                }
            }
        }

        get headers() {
            if (this.isTO1) {
                return [
                    {title: this.$t('department'), value: 'department'},
                    {title: this.$t('phone'), value: 'phone', sortable: true, key: 'PHONE'},
                    {title: this.$t('messenger'), value: 'messenger', sortable: true, key: 'ICQ'},
                ]
            }

            return []
        }
    }

    export default CompanySupplierPage
</script>
